import { CreditCardIcon } from '@heroicons/react/outline';
import { getConnectors } from 'api/v1/transactions';
import GridMasonryIcon from 'assets/svg/icons/GridMasonry';
import BankTab from 'components/atoms/SVG/BankTab';
import WalletEuroIcon from 'components/atoms/SVG/WalletEuroIcon.component';
import {
  isAccountableBank,
  isCustomSubAccount,
} from 'components/pages/Banks/Wallet/helpers';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import appI18nInstance from 'i18n';
import type { UseQueryOptions } from 'react-query';
import { useQuery } from 'react-query';
import { BankTransactionsCacheKeys } from 'types/cacheKeys.types';
import type { Connector, ConnectorUIProps } from 'types/transactions.types';

export const resolveConnectorUIProps = (
  connector: Connector,
): ConnectorUIProps => {
  const _isAccountableBank = isAccountableBank(connector);

  if (_isAccountableBank) {
    const isSpace = isCustomSubAccount(connector);

    return {
      headingTitle: appI18nInstance.t('transactions.title'),
      tabTitle: appI18nInstance.t('accountable_banking'),
      tabSubtitle: isSpace
        ? connector.bankAccountReference.name
        : appI18nInstance.t(`wallet.${connector.bankAccountReference.name}`),
      title: isSpace
        ? connector.bankAccountReference.name
        : appI18nInstance.t(`wallet.${connector.bankAccountReference.name}`),
      settingsEditableSlug: isSpace
        ? connector.bankAccountReference.name
        : appI18nInstance.t(`wallet.${connector.bankAccountReference.name}`),
      Icon: isSpace ? GridMasonryIcon : WalletEuroIcon,
    };
  }

  return {
    headingTitle: appI18nInstance.t('transactions.title'),
    tabTitle:
      connector.provider === 'credit-card'
        ? appI18nInstance.t('bank.credit_cards.label')
        : appI18nInstance.t(
            connector.bankAccountReference.financialInstitutionSlug || '',
          ),
    tabSubtitle: connector.bankAccountReference?.name
      ? `${connector.bankAccountReference?.name} ${
          connector.status === 'deprecated' ? '(Deprecated)' : ''
        }`
      : connector.bankAccountReference?.IBAN,
    title:
      connector.bankAccountReference?.name ||
      connector.bankAccountReference?.IBAN ||
      'unknown',
    settingsEditableSlug:
      connector.bankAccountReference.name ||
      connector.bankAccountReference.financialInstitutionSlug ||
      '',
    Icon:
      connector.provider === 'credit-card'
        ? CreditCardIcon
        : connector.provider === 'swan'
        ? GridMasonryIcon
        : BankTab,
  };
};

const useGetConnectors = (options?: UseQueryOptions) => {
  const { data: user } = useGetCurrentCustomerData();

  return useQuery({
    queryKey: BankTransactionsCacheKeys.Connectors,
    queryFn: getConnectors,
    enabled: !!user?._id,
    select: (data: any) => {
      const connectors = data.map((connector: Connector) => ({
        ...connector,
        ...resolveConnectorUIProps(connector),
      }));

      if (options?.select) return options.select(connectors);

      return connectors;
    },
    ...options,
  });
};

export default useGetConnectors;
